import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLandingSeasonHero } from "@/hooks/useLandingSeasonHero";
import { useTranslate } from "@/i18n";
import defaultHeroImage from "@/assets/landing-hero-v2.jpg";

/**
 * Landing v2 Hero — "Äkthet"
 * Seasonal hero image via landing_season_overlays + media_assets.
 * Falls back to static default if no overlay exists.
 * Single H1 on the entire page. Copy is season-invariant.
 */
const LandingHeroV2 = () => {
  const { imageUrl: seasonalUrl, altText, isLoading } = useLandingSeasonHero();
  const [seasonalLoaded, setSeasonalLoaded] = useState(false);
  const t = useTranslate();

  const hasSeasonalImage = !!seasonalUrl;
  const heroSrc = hasSeasonalImage ? seasonalUrl : defaultHeroImage;
  const heroAlt = altText ?? t("home.hero.imageAlt");

  const scrollToDirection = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById("direction")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background image — decorative only, must not intercept pointer events */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Default image always rendered to prevent CLS */}
        <img
          src={defaultHeroImage}
          alt={heroAlt}
          className="h-full w-full object-cover"
          style={{
            filter: "saturate(0.88) contrast(0.97) brightness(0.98)",
            opacity: hasSeasonalImage && seasonalLoaded ? 0 : 1,
            transition: "opacity 200ms ease-out",
          }}
          loading="eager"
          fetchPriority="high"
        />

        {/* Seasonal image layered on top with fade-in */}
        {hasSeasonalImage && (
          <img
            src={seasonalUrl}
            alt={heroAlt}
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              filter: "saturate(0.88) contrast(0.97) brightness(0.98)",
              opacity: seasonalLoaded ? 1 : 0,
              transition: "opacity 200ms ease-out",
            }}
            loading="eager"
            onLoad={() => setSeasonalLoaded(true)}
          />
        )}

        {/* Subtle dark overlay for text readability */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to right, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.10) 100%)" }}
        />
      </div>

      {/* Content — left-aligned */}
      <div className="container relative z-10 py-20 pb-22 md:py-24 md:pb-28 lg:py-32 lg:pb-36">
        <div className="max-w-xl animate-fade-in">
          <h1
            className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.12] tracking-[-0.01em]"
            style={{ color: "rgba(255,255,255,0.95)" }}
          >
            {t("home.hero.titleLine1")}
            <br />
            {t("home.hero.titleLine2")}
          </h1>

          <p
            className="mt-4 text-base sm:text-lg leading-[1.6] animate-fade-in"
            style={{ color: "rgba(255,255,255,0.88)", animationDelay: "60ms" }}
          >
            {t("home.hero.sub1")}
          </p>

          <p
            className="mt-3 text-lg sm:text-xl leading-[1.6] animate-fade-in"
            style={{ color: "rgba(255,255,255,0.82)", animationDelay: "80ms" }}
          >
            {t("home.hero.sub2")}
          </p>

          {/* CTAs */}
          <div
            className="mt-10 animate-fade-in"
            style={{ animationDelay: "160ms" }}
          >
            <Button
              size="lg"
              className="rounded-[24px] px-10 h-12 text-base bg-background text-foreground hover:bg-background/90 transition-colors duration-[160ms]"
              onClick={scrollToDirection}
            >
              {t("home.hero.cta")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHeroV2;
